import { message } from 'antd';
import _ from 'lodash';
import { showMessageOnError } from '../../../api/base.api';
import { useUserEditMutation } from '../../../api/user/user.api';
import { useLoadState } from '../../../hooks/useLoadState';
import { UserFullDto } from '../../../types';
import { googleApiSrc } from '../../../utils/google';
import { AnimatedModal } from '../AnimatedModal/AnimatedModal';
import { LoadScript } from '../LoadScript/LoadScript';
import { Null } from '../Null/Null';
import StandaloneLoader from '../StandaloneLoader';
import { AddressForm, addressFormFields, AddressFormValues } from './AddressForm';

function userToAddressForm(user: UserFullDto): AddressFormValues {
  return _.pick(user, addressFormFields);
}

export type AddressModalProps = {
  currentUser: UserFullDto;
  showModal: boolean;
  onSuccess: () => void;
  /**
   * Still need to provide `onCancel` even if `canCancel` is false because
   * it will be called regardless of `canCancel` if unrecoverable error happens.
   */
  onCancel: () => void;
  canCancel: boolean;
};

export function AddressModal({
  currentUser,
  showModal,
  onSuccess,
  onCancel,
  canCancel,
}: AddressModalProps) {
  const googlePlacesScriptControl = useLoadState({
    onError(err) {
      console.error('Error loading Google Places api.', err);
      message.error('Error loading Google Places api.');
      // doesn't matter if we `canCancel` or not, can't proceed if error with google api loading.
      onCancel();
    },
  });
  const usersEdit = useUserEditMutation({
    onSuccess(res) {
      if (res.isAddressVerified) {
        message.success('Address updated successfully.');
        onSuccess?.();
      } else {
        message.error('Address verification failed.');
      }
    },
    onError: showMessageOnError,
  });
  const defaultValues = userToAddressForm(currentUser);

  const isLoading = usersEdit.isLoading || googlePlacesScriptControl.isLoading;
  // null loader because we have shared StandaloneLoader
  const googlePlacesScriptLoader = Null;

  return (
    <AnimatedModal showModal={showModal} onBackdropClick={canCancel ? onCancel : undefined}>
      {isLoading && <StandaloneLoader />}
      <LoadScript
        control={googlePlacesScriptControl}
        src={googleApiSrc}
        loader={googlePlacesScriptLoader}
        fallback={<span className="text-danger">Error loading Google Places api.</span>}
      >
        <AddressForm
          defaultValues={defaultValues}
          onSubmit={usersEdit.mutate}
          isSubmitting={usersEdit.isLoading}
          onCancel={onCancel}
          canCancel={canCancel}
        />
      </LoadScript>
    </AnimatedModal>
  );
}
